import React, { useCallback, useMemo, useEffect } from 'react'
import { TouchableOpacity, View, StyleSheet } from 'react-native'
import { BtCheckBox } from '@linqpal/components/src/ui'
import { Text } from 'react-native-paper'
import Layout from '../Layout'
import { SignUpPhone } from '../../../assets/icons'
import { paths } from '../../links'
import { Spacer } from '../../../ui/atoms'
import { useTranslation } from 'react-i18next'
import Title from '../Title'
import { AlreadyHaveAcc } from '../AlreadyHaveAcc'
import useUtm from '../useUtm'
import { useResponsive } from '../../../utils/hooks'
import { BtText } from '@linqpal/components/src/ui'
import SignupStore from '../stores/SignupStore'
import PhoneNumberInput from '../../../ui/organisms/PhoneNumberInput'
import { Input } from '../../../ui/atoms/builder-2.0/Input'
import { observer } from 'mobx-react'
import SignInStore from '../stores/SignInStore'
import TermsAndConditions from '../TermsAndConditions'
import SubmitButton from '../SubmitButton'
import useRecaptcha from '../useRecaptcha'
import { ErrorMessage } from '../ErrorMessage'
enum Type {
  PHONE = 'code',
  EMAIL = 'password',
}

interface SignUpHomeProps {
  navigation: any
  route: any
  dontNavigate?: (arg: boolean) => void
  onContinue?: () => void
  onUserExists?: (args: any) => void
}

export default observer(
  ({
    navigation,
    route,
    dontNavigate,
    onContinue,
    onUserExists,
  }: SignUpHomeProps) => {
    const { t } = useTranslation('auth')
    useUtm(route.params)
    const { sm } = useResponsive()
    const recaptcha = useRecaptcha('signup')
    const {
      login,
      setLogin,
      type,
      isValidLogin,
      opt_for_marketing_messages,
      errors,
    } = SignupStore
    const { canAskOtp } = SignInStore

    useEffect(() => {
      canAskOtp && navigation.navigate(paths.Auth.SignInVerify)
    }, [canAskOtp, navigation])

    const handleUserExists = useCallback(() => {
      if (onUserExists) {
        onUserExists({
          type,
          login,
          isNewUser: false,
          verifier: recaptcha.verifier,
        })
      }
    }, [onUserExists, type, login, recaptcha.verifier])

    const pressContinue = useCallback(async () => {
      if (!isValidLogin) return
      SignupStore.checkUserSignUp(recaptcha.verifier)
        .then((data: any) => {
          if (onContinue) {
            return onContinue()
          }
          if (type === 'code') {
            return navigation.navigate(paths.Auth.SignUpVerify)
          }
          return navigation.navigate(paths.Auth.SignUpPassword)
        })
        .catch((e) => {
          SignInStore.setLogin(login)

          if (e === 'email-already-in-use' || e === 'phone-already-in-use') {
            if (!onContinue) {
              if (e === 'email-already-in-use') {
                navigation.navigate(paths.Auth.SignInPassword)
              } else {
                SignInStore.signIn(recaptcha.verifier)
              }
            }
            handleUserExists()
          }
        })
    }, [
      isValidLogin,
      recaptcha.verifier,
      type,
      navigation,
      onContinue,
      login,
      handleUserExists,
    ])

    const input = useMemo(() => {
      if (type === 'code') {
        return (
          <PhoneNumberInput
            customStyle={{ marginBottom: 0 }}
            testID="signUp_phone_input"
            label={t('signUp.loginLabelPhone')}
            value={login}
            onChangeText={(text) => {
              setLogin(text)
              SignupStore.removeError('login')
            }}
            autoFocus
            mandatory
          />
        )
      }
      return (
        <Input
          testID="signUp_email_input"
          value={login}
          onChangeText={(text) => {
            setLogin(text)
            SignupStore.removeError('login')
          }}
          autoFocus
          label={t('signUp.loginLabelEmail')}
          mandatory
        />
      )
    }, [type, login, setLogin, t])

    const checkBoxPress = () => {
      SignupStore.setValue(
        'opt_for_marketing_messages',
        !opt_for_marketing_messages,
      )
    }
    //Don't add any dependency
    useEffect(() => {
      SignupStore.removeError('login')
      SignupStore.removeError('verify')
      recaptcha.createVerifier()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      SignupStore.removeError('login')
    }, [type])

    return (
      <Layout showsBackButton={false}>
        <Title testID="signUp_title" style={{ color: '#003353' }}>
          {t('signUp.title')}
        </Title>
        <View style={{ marginTop: sm ? 40 : 24 }}>
          <View style={styles.tabsWrapper}>
            <SignInOption
              icon={<SignUpPhone />}
              text={t('signUp.homeOption1')}
              testID="signUp_with_phone_link"
              name={Type.PHONE}
              type={type}
              sm={sm}
            />
            <SignInOption
              icon={<SignUpPhone />}
              text={t('signUp.homeOption2')}
              testID="signUp_with_email_link"
              name={Type.EMAIL}
              type={type}
              sm={sm}
            />
          </View>
          <BtText
            style={[
              styles.captionLabel,
              {
                marginTop: sm ? 40 : 27,
                fontSize: sm ? 18 : 15,
              },
            ]}
          >
            {t(
              type === Type.PHONE ? 'phoneSignupCaption' : 'emailSignupCaption',
            )}
          </BtText>
          {input}
          <Spacer height={8} />
          {onContinue && (
            <ErrorMessage
              error={errors.get('login')?.message}
              onPress={() => {
                if (dontNavigate) {
                  dontNavigate(true)
                } else {
                  SignInStore.setLogin(login)
                  navigation.replace(paths.Auth.SignIn)
                }
              }}
              testID="signUp_error_message"
            />
          )}
          <TouchableOpacity
            style={[styles.checkBoxWrapper, { marginTop: sm ? 41 : 21 }]}
            onPress={checkBoxPress}
            activeOpacity={0.7}
          >
            <BtCheckBox
              style={{ width: 20, height: 20 }}
              checked={opt_for_marketing_messages}
              onPress={checkBoxPress}
            />
            <BtText style={[styles.checkBoxLabel, { fontSize: sm ? 14 : 12 }]}>
              {t('agreeToReceiveSms')}
            </BtText>
          </TouchableOpacity>
          <TermsAndConditions />
          <SubmitButton
            onPress={pressContinue}
            disabled={!isValidLogin}
            testID="signUp_submit_button"
          >
            {t('signUp.submitLabel2')}
          </SubmitButton>
          {sm && <Spacer height={20} />}
          <AlreadyHaveAcc
            navigation={navigation}
            dontNavigate={dontNavigate}
            testID="signup_screen_link_to_login"
          />
        </View>
      </Layout>
    )
  },
)
const SignInOption = observer(({ text, sm, name, type, ...props }) => {
  const { setValue, isInvitation, signupInvoiceId } = SignupStore

  const onSelect = () => {
    setValue('type', name)
    if (isInvitation || signupInvoiceId) {
      if (name === 'code') SignupStore.setLogin(SignupStore.phone)
      else SignupStore.setLogin(SignupStore.email)
    }
  }
  return (
    <TouchableOpacity
      onPress={onSelect}
      style={[
        styles.tab,
        {
          paddingVertical: !sm ? 6 : 12,
        },
        name === type && { backgroundColor: '#FFF' },
      ]}
      {...props}
    >
      <Text style={type === name ? styles.activeTabLabel : styles.tabLabel}>
        {text}
      </Text>
    </TouchableOpacity>
  )
})
const styles = StyleSheet.create({
  tabsWrapper: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0, 112, 170, 0.06)',
    borderRadius: 12,
    padding: 4,
    justifyContent: 'space-between',
  },
  tab: {
    width: '49%',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  tabLabel: {
    fontSize: 18,
    fontWeight: '500',
    lineHeight: 24,
    color: '#8CAEC3',
  },
  activeTabLabel: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 24,
    color: '#00A0F3',
  },
  captionLabel: {
    color: '#668598',
    lineHeight: 30,
  },
  checkBoxWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
  },
  checkBoxLabel: {
    lineHeight: 17,
    color: '#668598',
    marginLeft: 11,
  },
})
